import { getEffectiveFilterDefs, getAllKeys, isArrayDef, getDefByKey } from './filterDefinitions';

export const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'sqft', label: 'Square Feet' },
];

// Non-def state keys that flow through URL/state but are not rendered as filter fields.
const PASSTHROUGH_KEYS = ['q', 'cityId'];
const MAX_VALUE_LEN = 128;

function arrayKeys() {
  return getEffectiveFilterDefs().filter(isArrayDef).map((d) => d.key);
}

export const emptyFilters = () => {
  const f = {};
  const arr = new Set(arrayKeys());
  getAllKeys().forEach((k) => { f[k] = arr.has(k) ? [] : ''; });
  PASSTHROUGH_KEYS.forEach((k) => { f[k] = ''; });
  return f;
};

// Map legacy URL params (older filter model) onto the current def keys.
function applyLegacyParams(searchParams, f) {
  const isArr = new Set(arrayKeys());
  const setArr = (k, v) => { if (isArr.has(k)) { const cur = Array.isArray(f[k]) ? f[k] : []; if (!cur.includes(v)) f[k] = [...cur, v]; } };
  if (!f.minBeds) { const v = searchParams.get('beds'); if (v != null) f.minBeds = v; }
  if (!f.minBaths) { const v = searchParams.get('baths'); if (v != null) f.minBaths = v; }
  if (!f.minYearBuilt) { const v = searchParams.get('minYear'); if (v != null) f.minYearBuilt = v; }
  const legacyType = searchParams.get('type');
  if (legacyType && (!f.propertyType || !f.propertyType.length)) setArr('propertyType', legacyType.toLowerCase());
  const legacyStatus = searchParams.get('status');
  if (legacyStatus && (!f.status || !f.status.length)) {
    const map = { for_sale: 'A', for_rent: 'A', sold: 'Sld', new_construction: 'A' };
    if (map[legacyStatus]) {
      setArr('status', map[legacyStatus]);
      if (legacyStatus === 'for_sale') setArr('listingType', 'Sale');
      if (legacyStatus === 'for_rent') setArr('listingType', 'Rent');
    }
  }
}

// Parse URLSearchParams into a filters object.
export function paramsToFilters(searchParams) {
  const f = emptyFilters();
  const isArr = new Set(arrayKeys());
  getAllKeys().forEach((key) => {
    const v = searchParams.get(key);
    if (v == null) return;
    if (isArr.has(key)) f[key] = v.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 20);
    else f[key] = v.slice(0, MAX_VALUE_LEN);
  });
  PASSTHROUGH_KEYS.forEach((key) => {
    const v = searchParams.get(key);
    if (v != null) f[key] = v.slice(0, MAX_VALUE_LEN);
  });
  applyLegacyParams(searchParams, f);

  const LEGACY_STATUS = {
    for_sale: { status: 'A', listingType: 'Sale' },
    for_rent: { status: 'A', listingType: 'Rent' },
    sold: { status: 'Sld' },
    new_construction: { status: 'A' },
  };
  if (Array.isArray(f.status) && f.status.some((s) => LEGACY_STATUS[s])) {
    const next = new Set();
    f.status.forEach((s) => {
      const m = LEGACY_STATUS[s];
      if (m) {
        next.add(m.status);
        if (m.listingType) {
          if (!Array.isArray(f.listingType)) f.listingType = [];
          if (!f.listingType.includes(m.listingType)) f.listingType.push(m.listingType);
        }
      } else {
        next.add(s);
      }
    });
    f.status = [...next];
  }

  // Normalize old base:suffix tokens (e.g. "U:Sld" -> "Sld")
  if (Array.isArray(f.status)) {
    f.status = f.status.map((s) => (s.includes(':') ? s.split(':').pop() : s));
  }

  // Primary mode defaults to For Sale unless URL already specifies Rent (or both).
  if (!Array.isArray(f.listingType) || f.listingType.length === 0) {
    f.listingType = ['Sale'];
  }

  return f;
}

// Serialize a filters object into a plain params object (omit empties).
export function filtersToParams(filters) {
  const params = {};
  const keys = [...getAllKeys(), ...PASSTHROUGH_KEYS];
  keys.forEach((key) => {
    const value = filters[key];
    if (Array.isArray(value)) {
      if (value.length) params[key] = value.join(',');
    } else if (value !== '' && value != null) {
      params[key] = String(value);
    }
  });
  return params;
}

// listingType is controlled by the For Sale / For Rent toggle — not a chip/badge count.
const TOGGLE_EXCLUDED_KEYS = new Set(['listingType']);

export function countActiveFilters(filters) {
  let n = 0;
  const counted = new Set();
  getEffectiveFilterDefs().forEach((def) => {
    if (def.keys) {
      if (def.keys.some((k) => filters[k] !== '' && filters[k] != null)) n += 1;
      return;
    }
    const key = def.key;
    if (!key || counted.has(key) || TOGGLE_EXCLUDED_KEYS.has(key)) return;
    counted.add(key);
    const v = filters[key];
    if (Array.isArray(v)) n += v.length;
    else if (v !== '' && v != null) n += 1;
  });
  return n;
}

export { getDefByKey };
